import React, { useEffect, useMemo, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../api";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeContext } from "../ThemeContext";
import { Users, Gamepad2, Trophy } from "lucide-react";
import Dropdown from "../components/Dropdown";

// Card component matching GameOptionsPage design - defined outside to prevent recreation
const Card = React.memo(({ children, className = '', delay = 0, index = 0, shouldAnimate = true }) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasInputFocusRef = React.useRef(false);
  const cardRef = React.useRef(null);
  const hasAnimatedRef = React.useRef(!shouldAnimate);
  const { isDarkMode } = useContext(ThemeContext);
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  // Mark animation as complete after it finishes
  React.useEffect(() => {
    if (shouldAnimate && !hasAnimatedRef.current) {
      const timer = setTimeout(() => {
        hasAnimatedRef.current = true;
      }, (delay + 0.6) * 1000);
      return () => clearTimeout(timer);
    }
  }, [shouldAnimate, delay]);

  // Check if any input/textarea/select has focus within the card
  React.useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleFocusIn = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        hasInputFocusRef.current = true;
        setIsHovered(false); // Disable hover immediately
      }
    };

    const handleFocusOut = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        // Small delay to check if another input received focus
        setTimeout(() => {
          const activeElement = document.activeElement;
          if (!card.contains(activeElement) || 
              (activeElement.tagName !== 'INPUT' && 
               activeElement.tagName !== 'TEXTAREA' && 
               activeElement.tagName !== 'SELECT')) {
            hasInputFocusRef.current = false;
          }
        }, 10);
      }
    };

    const checkIfInputFocused = () => {
      const activeElement = document.activeElement;
      return card.contains(activeElement) && 
             (activeElement.tagName === 'INPUT' || 
              activeElement.tagName === 'TEXTAREA' || 
              activeElement.tagName === 'SELECT');
    };

    // Continuously check for input focus to disable hover - use a more aggressive approach
    const checkInterval = setInterval(() => {
      const isFocused = checkIfInputFocused();
      if (isFocused) {
        hasInputFocusRef.current = true;
        // Always disable hover when input is focused
        setIsHovered(false);
      } else {
        hasInputFocusRef.current = false;
      }
    }, 16); // Check every 16ms (60fps) for immediate response

    // Also listen to input events to catch typing
    const handleInput = () => {
      if (checkIfInputFocused()) {
        hasInputFocusRef.current = true;
        setIsHovered(false);
      }
    };

    card.addEventListener('focusin', handleFocusIn, true);
    card.addEventListener('focusout', handleFocusOut, true);
    card.addEventListener('input', handleInput, true);
    card.addEventListener('keydown', handleInput, true);
    card.addEventListener('keyup', handleInput, true);

    return () => {
      clearInterval(checkInterval);
      card.removeEventListener('focusin', handleFocusIn, true);
      card.removeEventListener('focusout', handleFocusOut, true);
      card.removeEventListener('input', handleInput, true);
      card.removeEventListener('keydown', handleInput, true);
      card.removeEventListener('keyup', handleInput, true);
    };
  }, []);
  
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  const checkIfInputFocusedNow = () => {
    if (!cardRef.current) return false;
    const activeElement = document.activeElement;
    return cardRef.current.contains(activeElement) && 
           (activeElement.tagName === 'INPUT' || 
            activeElement.tagName === 'TEXTAREA' || 
            activeElement.tagName === 'SELECT');
  };
  
  const isInputFocused = hasInputFocusRef.current || checkIfInputFocusedNow();
  const shouldShowHover = isHovered && !isInputFocused;
  
  return (
    <motion.div
      ref={cardRef}
      initial={hasAnimatedRef.current || !shouldAnimate ? false : { opacity: 0, y: 50, scale: 0.9 }}
      animate={{ 
        opacity: 1, 
        y: 0, 
        scale: 1
      }}
      transition={hasAnimatedRef.current || !shouldAnimate ? { duration: 0 } : {
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      style={{
        boxShadow: shouldShowHover
          ? `0 20px 60px ${hexToRgba(hoverColor, 0.25)}, 0 0 40px ${hexToRgba(hoverColor, 0.2)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: shouldShowHover
          ? hexToRgba(hoverColor, 0.4)
          : isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
        transition: isInputFocused ? 'none' : 'box-shadow 0.3s ease, border-color 0.3s ease',
      }}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border ${className}`}
      onMouseEnter={(e) => {
        // Immediately check if input is focused before allowing hover
        if (hasInputFocusRef.current || checkIfInputFocusedNow()) {
          setIsHovered(false);
          return;
        }
        // Check if target or its parents are inputs/form elements
        let target = e.target;
        let checkDepth = 0;
        while (target && target !== cardRef.current && checkDepth < 5) {
          const tagName = target.tagName;
          if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT' || 
              target.closest('input, textarea, select')) {
            setIsHovered(false);
            return;
          }
          target = target.parentElement;
          checkDepth++;
        }
        // Only set hover if no inputs are focused
        if (!hasInputFocusRef.current && !checkIfInputFocusedNow()) {
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      onMouseMove={(e) => {
        // Immediately disable hover if mouse moves over or near inputs
        if (hasInputFocusRef.current || checkIfInputFocusedNow()) {
          setIsHovered(false);
          return;
        }
        let target = e.target;
        let checkDepth = 0;
        while (target && target !== cardRef.current && checkDepth < 5) {
          const tagName = target.tagName;
          if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT' || 
              target.closest('input, textarea, select')) {
            setIsHovered(false);
            return;
          }
          target = target.parentElement;
          checkDepth++;
        }
      }}
    >
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 pointer-events-none"
        animate={{
          opacity: shouldShowHover ? 0.2 : 0,
        }}
        transition={{ duration: 0.3 }}
        style={{
          background: `linear-gradient(135deg, ${hexToRgba(hoverColor, 0.25)}, ${hexToRgba(hoverColor, 0.4)})`,
          filter: 'blur(20px)',
          zIndex: -1,
        }}
      />
      {children}
    </motion.div>
  );
});
Card.displayName = 'Card';

const MultiplayerLobby = () => {
  const navigate = useNavigate();
  const [roomCode, setRoomCode] = useState("");
  const [newRoomName, setNewRoomName] = useState("");
  const [newRoomSubject, setNewRoomSubject] = useState("All Content");
  const [isPrivate, setIsPrivate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [isInRoom, setIsInRoom] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [isCheckingMembership, setIsCheckingMembership] = useState(true);
  const [joinError, setJoinError] = useState("");
  const [isCheckingJoin, setIsCheckingJoin] = useState(false);
  const [privatePassword, setPrivatePassword] = useState("");
  const [joinPassword, setJoinPassword] = useState("");
  const [newRoomCapacity, setNewRoomCapacity] = useState(8);
  const baseUrlRaw = process.env.REACT_APP_COLYSEUS_URL || '';
  // Sanitize to avoid trailing slash which may cause double slashes in paths
  const baseUrl = baseUrlRaw.replace(/\/+$/,'');
  const userStr = typeof window !== 'undefined' ? localStorage.getItem('sat_user') : null;
  const user = userStr ? JSON.parse(userStr) : null;

  const [availableRooms, setAvailableRooms] = useState([]);
  const [roomsError, setRoomsError] = useState("");
  const location = useLocation();
  const [roomsSearch, setRoomsSearch] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("All");
  const [recentGames, setRecentGames] = useState([]);
  const [recentGamesLoading, setRecentGamesLoading] = useState(false);
  const [newRoomDifficulty, setNewRoomDifficulty] = useState('');
  const [hasChosenDifficulty, setHasChosenDifficulty] = useState(false);
  const [showDifficultyPopup, setShowDifficultyPopup] = useState(false);
  // Track if this is the first render to prevent double animations
  // Use a combination of ref and sessionStorage to persist across re-renders
  const getHasAnimated = () => {
    if (typeof window === 'undefined') return false;
    try {
      return sessionStorage.getItem('multiplayerLobby_hasAnimated') === 'true';
    } catch {
      return false;
    }
  };

  const setHasAnimated = (value) => {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem('multiplayerLobby_hasAnimated', value ? 'true' : 'false');
    } catch {}
  };

  // Initialize state based on sessionStorage - this persists across re-renders
  const hasAnimatedBefore = getHasAnimated();
  const [shouldAnimateCards, setShouldAnimateCards] = useState(!hasAnimatedBefore);
  const [shouldAnimateRecentGames, setShouldAnimateRecentGames] = useState(!hasAnimatedBefore);
  const [shouldAnimateRoomsTable, setShouldAnimateRoomsTable] = useState(!hasAnimatedBefore);
  const animationStartedRef = React.useRef(false);
  const { isDarkMode } = useContext(ThemeContext);

  // Memoize gradient for title and section headings
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  const sectionHeadingGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  // Mark animations as started and complete after they finish
  React.useEffect(() => {
    // Only allow animation on first mount if not already animated
    if (!hasAnimatedBefore && !animationStartedRef.current) {
      animationStartedRef.current = true;
      
      // Mark as complete after animations finish
      const timer = setTimeout(() => {
        setShouldAnimateCards(false);
        setShouldAnimateRecentGames(false);
        setShouldAnimateRoomsTable(false);
        setHasAnimated(true);
      }, 2500);
      
      return () => clearTimeout(timer);
    }
  }, [hasAnimatedBefore]);


  // Fetch recent games
  const fetchRecentGames = async () => {
    if (!user?.user_id) {
      setRecentGamesLoading(false);
      return;
    }

    try {
      setRecentGamesLoading(true);
      const response = await api.get(`/api/compete/get_recent_games/`);
      setRecentGames(response.data?.recent_games || []);
    } catch (error) {
      console.error('Error fetching recent games:', error);
      setRecentGames([]);
    } finally {
      setRecentGamesLoading(false);
    }
  };

  // Check if user is already in a room (using Colyseus real-time check)
  const checkUserRoomMembership = async () => {
    if (!user?.user_id) {
      setIsCheckingMembership(false);
      return;
    }

    try {
      const response = await fetch(`${baseUrl}/user/${encodeURIComponent(user.user_id)}/room-membership`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();

      if (data.is_in_room) {
        setIsInRoom(true);
        setCurrentRoom({
          room_id: data.room_id,
          room_name: data.room_name,
          room_status: data.room_status,
          is_host: data.is_host
        });
      } else {
        setIsInRoom(false);
        setCurrentRoom(null);
      }
    } catch (error) {
      console.error('Error checking room membership:', error);
      setIsInRoom(false);
      setCurrentRoom(null);
    } finally {
      setIsCheckingMembership(false);
    }
  };

  // Check room membership and fetch total score on component load
  useEffect(() => {
    if (user?.user_id) {
    checkUserRoomMembership();
    fetchRecentGames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Refresh room membership and score when component becomes visible (user returns from room)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkUserRoomMembership();
        fetchRecentGames();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.user_id]);

  useEffect(() => {
    let mounted = true;
    // surface error passed from redirects
    if (location?.state?.error && mounted) {
      setRoomsError(location.state.error);
      // clear error state so it doesn't persist on navigation
      // Use setTimeout to prevent immediate re-render
      setTimeout(() => {
      navigate(location.pathname, { replace: true, state: {} });
      }, 0);
    }
    const loadRooms = async () => {
      try {
        if (!mounted) return;
        const res = await fetch(`${baseUrl}/rooms`);
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const data = await res.json();
        if (!mounted) return;
        const mappedAll = (Array.isArray(data) ? data : []).map((r) => ({
          id: r.roomId,
          subject: r.subject || 'All',
          difficulty: r.difficulty || 'Easy',
          players: r.players ?? 0,
          capacity: r.maxClients ?? 8,
          visibility: (r.visibility || 'public').charAt(0).toUpperCase() + (r.visibility || 'public').slice(1),
          name: r.name || 'Room',
          status: r.status || null,
        }));
        const mapped = mappedAll.filter(r => (r.players ?? 0) < (r.capacity ?? 0));
        setAvailableRooms(mapped);
        setRoomsError("");
      } catch (e) {
        if (!mounted) return;
        setRoomsError(e.message || 'Failed to load rooms');
      }
    };
    loadRooms();
    const t = setInterval(loadRooms, 5000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, [location, navigate]);

  const subjectOptions = useMemo(() => {
    const set = new Set(["All"]);
    availableRooms.forEach(r => { if (r.subject) set.add(r.subject); });
    return Array.from(set);
  }, [availableRooms]);

  const filteredRooms = useMemo(() => {
    const q = roomsSearch.trim().toLowerCase();
    return availableRooms.filter(r => {
      const matchesSubject = subjectFilter === "All" || r.subject === subjectFilter;
      const isCompleted = r.status === 'completed';
      const isInProgress = r.status === 'in_progress';
      const isPublic = (r.visibility || '').toLowerCase() === 'public';

      // Hide completed rooms, but show in-progress public rooms
      if (isCompleted) return false;
      if (isInProgress && !isPublic) return false; // Hide in-progress private rooms

      if (!matchesSubject) return false;
      if (q === "") return true;
      return (r.name?.toLowerCase().includes(q)) || (r.subject?.toLowerCase().includes(q));
    });
  }, [availableRooms, roomsSearch, subjectFilter]);

  // Handle clicking on a recent game to view its report
  const handleRecentGameClick = (gameId) => {
    navigate(`/multiplayer/report/${gameId}`);
  };

  const handleJoinByCode = async () => {
    const code = roomCode.trim();
    setJoinError("");
    if (!code) return;
    try {
      setIsCheckingJoin(true);
      const res = await fetch(`${baseUrl}/rooms/${encodeURIComponent(code)}`);
      if (!res.ok) throw new Error('Room does not exist');
      const data = await res.json();
      console.log('data', data?.clients.length, data?.maxClients);
      if ((data?.clients?.length ?? 0) >= (data?.maxClients ?? 0)) {
        setJoinError('Room is full');
        return;
      }
      else if(data.status==='completed'){
        setJoinError('Room is locked. Game already completed.');
        return;
      }
      // Allow joining in-progress rooms (player will be put in waiting state)
      if (data.visibility === 'private') {
        if (!joinPassword) {
          setJoinError('Enter password for private room');
          return;
        }
        const v = await fetch(`${baseUrl}/rooms/${encodeURIComponent(code)}/validate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: joinPassword })
        });
        const vjson = await v.json();
        if (!v.ok || !vjson.ok) {
          setJoinError('Incorrect password');
          return;
        }
        // double check capacity just before navigation to avoid races
        const res2 = await fetch(`${baseUrl}/rooms/${encodeURIComponent(code)}`);
        if (res2.ok) {
          const d2 = await res2.json();
          if ((d2?.clients ?? 0) >= (d2?.maxClients ?? 0)) {
            setJoinError('Room is full');
            return;
          }
        }
      }
      navigate(`/multiplayer/waiting-room/${encodeURIComponent(code)}`, {
        state: {
          fromLobby: true,
          passwordVerified: true,
          roomId: code,
        }
      });
    } catch (e) {
      setJoinError('Room does not exist');
    } finally {
      setIsCheckingJoin(false);
    }
  };

  const handleCreateRoom = async () => {
    setCreateError("");


    // Check if user is already in a room
    if (isInRoom) {
      setCreateError('You are already in a room. Please leave your current room before creating a new one.');
      return;
    }

    if (!newRoomName.trim() || !newRoomSubject) return;
    if (isPrivate && !privatePassword.trim()) {
      setCreateError('Please set a password for private room');
      return;
    }
    try {
      setIsCreating(true);
        const res = await fetch(`${baseUrl}/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newRoomName.trim(),
          subject: newRoomSubject,
            difficulty: newRoomDifficulty,
          visibility: isPrivate ? 'private' : 'public',
          maxClients: newRoomCapacity,
          password: isPrivate ? privatePassword.trim() : undefined,
          admin: {
            id: user?.user_id ?? null,
            name: (user?.screenName || user?.screen_name || user?.user_name) ?? 'Admin',
          }
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || `Failed with ${res.status}`);
      }
      const data = await res.json();
      const room_created= await api.post(`/api/compete/create_room/`, {
        room_id: data.roomId,
        name: newRoomName.trim(),
        subject: newRoomSubject,
        difficulty: newRoomDifficulty,
        is_private: isPrivate,
        passcode: privatePassword.trim(),
      });
      console.log(room_created);
      navigate(`/multiplayer/waiting-room/${encodeURIComponent(data.roomId)}`, {
        state: {
          fromLobby: true,
          passwordVerified: true,
          roomId: data.roomId,

        }
      });
    } catch (e) {
      setCreateError(e.message || 'Failed to create room');
    } finally {
      setIsCreating(false);
    }
  };

  const handleJoinListedRoom = (roomId) => {
    const room = availableRooms.find(r => r.id === roomId);
    const isPrivate = room ? (room.visibility || '').toLowerCase() === 'private' : false;
    navigate(`/multiplayer/waiting-room/${encodeURIComponent(roomId)}`, {
      state: {
        fromLobby: true,
        passwordVerified: !isPrivate,
        roomId: roomId,
      }
    });
  };

  return (
    <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6">
        {/* Animated Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8"
        >
          <h1
            key={`title-${isDarkMode}`}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
          Compete Mode
        </h1>
          <motion.p
            className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 font-zonaProRegular"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Join or create rooms to compete with other players
          </motion.p>
        </motion.div>


        <div className="w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-6 gap-6">
        <div className="lg:col-span-3">
            <Card delay={0.1} index={0} className="space-y-8" shouldAnimate={shouldAnimateCards}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Join by Code */}
              <div onMouseEnter={(e) => e.stopPropagation()} onMouseLeave={(e) => e.stopPropagation()} onMouseMove={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-3 mb-4">
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="p-2 rounded-xl bg-[#a6a6a6] text-white"
                    >
                      <Users size={20} />
                    </motion.div>
                    <h2
                      key={`join-by-code-${isDarkMode}`}
                      className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-left relative inline-block"
                      style={{
                        color: isDarkMode ? '#ff7a00' : '#212D82',
                        background: sectionHeadingGradient,
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        animation: 'shine 10s linear infinite',
                        lineHeight: '1.2',
                        paddingBottom: '0.25rem'
                      }}
                    >
                      Join by Code
                    </h2>
                  </div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 text-left mb-2">Enter Room Code</label>
                <input
                  value={roomCode}
                  onChange={(e) => setRoomCode(e.target.value)}
                  onMouseEnter={(e) => e.stopPropagation()}
                  onMouseLeave={(e) => e.stopPropagation()}
                  onMouseMove={(e) => e.stopPropagation()}
                  placeholder="e.g., r-42Q7A9"
                    className="w-full px-4 py-3 rounded-lg bg-transparent border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-orange-500"
                />
                 {/* Private join password input */}
                 <input
                  value={joinPassword}
                  onChange={(e) => setJoinPassword(e.target.value)}
                  onMouseEnter={(e) => e.stopPropagation()}
                  onMouseLeave={(e) => e.stopPropagation()}
                  onMouseMove={(e) => e.stopPropagation()}
                  placeholder="Password (private rooms)"
                    className="mt-3 w-full px-4 py-3 rounded-lg bg-transparent border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-orange-500"
                  type="password"
                  name="join-password"
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
                  <motion.button
                  onClick={handleJoinByCode}
                  disabled={isCheckingJoin || !roomCode.trim()}
                    className="mt-4 w-full py-3 px-6 bg-[#34c759] text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                  {isCheckingJoin ? 'Checking…' : 'Join'}
                  </motion.button>
                {joinError && (
                    <div className="mt-2 text-red-600 dark:text-red-400 text-sm">{joinError}</div>
                )}
              </div>

              {/* Create Room */}
              <div onMouseEnter={(e) => e.stopPropagation()} onMouseLeave={(e) => e.stopPropagation()} onMouseMove={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-3 mb-4">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      className="p-2 rounded-xl bg-[#a6a6a6] text-white"
                    >
                      <Gamepad2 size={20} />
                    </motion.div>
                    <h2
                      key={`create-room-${isDarkMode}`}
                      className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-left relative inline-block"
                      style={{
                        color: isDarkMode ? '#ff7a00' : '#212D82',
                        background: sectionHeadingGradient,
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        animation: 'shine 10s linear infinite',
                        lineHeight: '1.2',
                        paddingBottom: '0.25rem'
                      }}
                    >
                      Create Room
                    </h2>
                  </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm text-left text-gray-600 dark:text-gray-400 mb-2">Room Name</label>
                      <input
                        value={newRoomName}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 24) {
                            setNewRoomName(value);
                          }
                        }}
                        onMouseEnter={(e) => e.stopPropagation()}
                        onMouseLeave={(e) => e.stopPropagation()}
                        onMouseMove={(e) => e.stopPropagation()}
                        maxLength={24}
                          className="w-full px-4 py-3 rounded-lg bg-transparent border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-orange-500"
                      />
                      <p className={`text-xs mt-1 text-right ${newRoomName.length === 24 ? 'text-red-500 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                        {newRoomName.length}/24 {newRoomName.length === 24 && '(Limit reached)'}
                      </p>
                    </div>
                    <div>
                        <label className="block text-sm text-left text-gray-600 dark:text-gray-400 mb-2">Players</label>
                      <Dropdown
                        options={[4, 6, 8]}
                        selectedOption={newRoomCapacity}
                        onOptionChange={(value) => setNewRoomCapacity(Number(value))}
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 text-left mb-2">Subject</label>
                    <Dropdown
                      options={[
                        "All Content",
                        "All Math",
                        "Algebra",
                        "Advanced Math",
                        "Problem Solving and Data Analysis",
                        "Geometry and Trigonometry",
                        "All English",
                        "Reading",
                        "Writing",
                        "Sentence Completion"
                      ]}
                      selectedOption={newRoomSubject}
                      onOptionChange={setNewRoomSubject}
                      className="w-full"
                      scrollable={true}
                    />
                  </div>
                  <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 text-left mb-2">Difficulty</label>
                      {!hasChosenDifficulty ? (
                        <motion.button
                          type="button"
                          onClick={() => setShowDifficultyPopup(true)}
                          className="w-full py-3 px-6 bg-[#34c759] text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Select Difficulty
                        </motion.button>
                      ) : (
                        <div className="flex items-center gap-3">
                          <span className="w-full px-4 py-3 rounded-lg bg-transparent border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 font-zonaProRegular">{newRoomDifficulty}</span>
                          <motion.button
                            type="button"
                            onClick={() => setShowDifficultyPopup(true)}
                            className="flex-1 py-3 px-6 bg-[#34c759] text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            Change
                          </motion.button>
                        </div>
                      )}
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={isPrivate}
                        onChange={(e) => setIsPrivate(e.target.checked)}
                          className="h-4 w-4 accent-cyan-500 dark:accent-orange-500"
                      />
                        <span className="text-gray-800 dark:text-gray-200">Private</span>
                    </label>
                      <span className="text-gray-600 dark:text-gray-400 text-sm">{isPrivate ? 'Private room' : 'Public room'}</span>
                  </div>
                  {isPrivate && (
                    <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-400 text-left mb-2">Password</label>
                      <input
                        type="password"
                        value={privatePassword}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.length <= 16) {
                            setPrivatePassword(value);
                          }
                        }}
                        onMouseEnter={(e) => e.stopPropagation()}
                        onMouseLeave={(e) => e.stopPropagation()}
                        onMouseMove={(e) => e.stopPropagation()}
                        maxLength={16}
                        placeholder="Set a room password"
                          className="w-full px-4 py-3 rounded-lg bg-transparent border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-orange-500"
                        name="create-room-password"
                        autoComplete="new-password"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                      />
                      <p className={`text-xs mt-1 text-right ${privatePassword.length === 16 ? 'text-red-500 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                        {privatePassword.length}/16 {privatePassword.length === 16 && '(Limit reached)'}
                      </p>
                    </div>
                  )}
                    <motion.button
                    onClick={handleCreateRoom}
                      disabled={!newRoomName.trim() || !newRoomSubject || !hasChosenDifficulty || isInRoom || isCheckingMembership}
                      className="w-full py-3 px-6 bg-[#34c759] text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                  >
                    {isCheckingMembership ? 'Checking...' : isInRoom ? 'Already in Room' : 'Create Room'}
                    </motion.button>
                  {createError && (
                      <div className="mt-2 text-red-600 dark:text-red-400 text-sm">{createError}</div>
                  )}
                </div>
              </div>
            </div>
              
                         {isInRoom && currentRoom && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 text-center border border-yellow-200 dark:border-yellow-800 rounded-lg"
                >
                     <div className="flex items-center justify-center">
                       <div>
                      <p className="text-yellow-800 dark:text-yellow-200 font-medium">You are already in a room</p>
                      <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                           Room: <span className="font-semibold">{currentRoom.room_name}</span>
                        {currentRoom.is_host && <span className="ml-2 text-xs bg-yellow-200 dark:bg-yellow-800 px-2 py-1 rounded">Host</span>}
                         </p>
                      <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
                           Please check your other tabs or windows to access your room.
                         </p>
                       </div>
                     </div>
                </motion.div>
                 )}

            {/* Recent Games */}
            <div>
                <div className="flex items-center gap-3 mb-4">
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="p-2 rounded-xl bg-gradient-to-br from-gray-400 to-gray-500 text-white"
                  >
                    <Trophy size={20} />
                  </motion.div>
                  <h3
                    key={`recent-games-${isDarkMode}`}
                    className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-left relative inline-block"
                    style={{
                      color: isDarkMode ? '#ff7a00' : '#212D82',
                      background: sectionHeadingGradient,
                      backgroundSize: '200% 100%',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      animation: 'shine 10s linear infinite',
                      lineHeight: '1.2',
                      paddingBottom: '0.25rem'
                    }}
                  >
                    Recent Games
                  </h3>
                </div>
              {recentGamesLoading ? (
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 dark:border-orange-500"></div>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">Loading recent games...</span>
                </div>
              ) : recentGames.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No recent games found.</p>
                  <p className="text-sm">Play some games to see them here!</p>
                </div>
              ) : (
                <div className="overflow-x-auto h-64 overflow-y-auto">
                    <table className="min-w-full text-left text-gray-800 dark:text-gray-200" style={{ height: 'auto' }}>
                      <thead className="sticky top-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm z-10">
                        <tr>
                          <th className="py-2 pr-4 border-b border-gray-200 dark:border-gray-700">Game Name</th>
                          <th className="py-2 pr-4 border-b border-gray-200 dark:border-gray-700">Subject</th>
                          <th className="py-2 pr-4 border-b border-gray-200 dark:border-gray-700">Players</th>
                          <th className="py-2 pr-4 border-b border-gray-200 dark:border-gray-700">Your Score</th>
                          <th className="py-2 pr-4 border-b border-gray-200 dark:border-gray-700">Your Rank</th>
                          <th className="py-2 pr-4 border-b border-gray-200 dark:border-gray-700">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                        {recentGames.map((game, idx) => (
                          <motion.tr
                          key={game.game_id}
                            initial={!shouldAnimateRecentGames ? false : { opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={!shouldAnimateRecentGames ? { duration: 0 } : { delay: idx * 0.1 }}
                            className="border-t border-gray-200 dark:border-gray-700 hover:bg-cyan-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                          onClick={() => handleRecentGameClick(game.game_id)}
                        >
                            <td className="py-3 pr-4 whitespace-nowrap font-medium">{game.game_name}</td>
                          <td className="py-3 pr-4 whitespace-nowrap">{game.game_subject}</td>
                          <td className="py-3 pr-4">{game.game_players}</td>
                            <td className={`py-3 pr-4 font-semibold ${game.user_score >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {game.user_score > 0 ? `+${game.user_score}` : game.user_score}
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                game.user_rank === 1 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                game.user_rank === 2 ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                                game.user_rank === 3 ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' :
                                'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                            }`}>
                              #{game.user_rank}
                            </span>
                          </td>
                            <td className="py-3 pr-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                            {new Date(game.game_date).toLocaleDateString()}
                          </td>
                          </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </Card>
        </div>

        <div className="lg:col-span-3">
            <Card delay={0.2} index={1} shouldAnimate={shouldAnimateCards}>
            <div className="flex items-end justify-between mb-4" style={{ pointerEvents: 'auto' }}>
              <div>
                  <h2
                    key={`available-rooms-${isDarkMode}`}
                    className="mt-6 text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra relative inline-block"
                    style={{
                      color: isDarkMode ? '#ff7a00' : '#212D82',
                      background: sectionHeadingGradient,
                      backgroundSize: '200% 100%',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      animation: 'shine 10s linear infinite',
                      lineHeight: '1.2',
                      paddingBottom: '0.25rem'
                    }}
                  >
                    Available Rooms
                  </h2>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 text-left font-zonaProRegular">Browse and join a room to play.</p>
              </div>
            </div>

            {/* Filters */}
            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3" style={{ pointerEvents: 'auto' }} onMouseEnter={(e) => e.stopPropagation()} onMouseLeave={(e) => e.stopPropagation()} onMouseMove={(e) => e.stopPropagation()}>
              <div className="md:col-span-2">
                <input
                  value={roomsSearch}
                  onChange={(e) => setRoomsSearch(e.target.value)}
                  onMouseEnter={(e) => e.stopPropagation()}
                  onMouseLeave={(e) => e.stopPropagation()}
                  onMouseMove={(e) => e.stopPropagation()}
                  placeholder="Search by room name or subject"
                    className="w-full px-4 py-3 rounded-lg bg-transparent border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-orange-500"
                />
              </div>
              <div>
                <Dropdown
                  options={subjectOptions}
                  selectedOption={subjectFilter}
                  onOptionChange={setSubjectFilter}
                  className="w-full"
                />
              </div>
            </div>

              <div className="overflow-x-auto relative">
                <table className="min-w-full text-left text-gray-800 dark:text-gray-200" style={{ height: 'auto' }}>
                  <thead className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
                    <tr>
                      <th className="py-2 pr-4 border-b border-gray-200 dark:border-gray-700">Room Name</th>
                      <th className="py-2 pr-4 border-b border-gray-200 dark:border-gray-700">Subject</th>
                      <th className="py-2 pr-4 border-b border-gray-200 dark:border-gray-700">Difficulty</th>
                      <th className="py-2 pr-4 border-b border-gray-200 dark:border-gray-700">Players</th>
                      <th className="py-2 pr-4 border-b border-gray-200 dark:border-gray-700">Visibility</th>
                      <th className="py-2 pr-4 text-right border-b border-gray-200 dark:border-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                    {roomsError && (
                      <motion.tr
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <td className="py-3 pr-4 text-red-600 dark:text-red-400" colSpan={6}>{roomsError}</td>
                      </motion.tr>
                    )}
                    {!roomsError && filteredRooms.length === 0 && (
                      <motion.tr
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <td className="py-3 pr-4 text-gray-500 dark:text-gray-400" colSpan={6}>No active rooms</td>
                      </motion.tr>
                    )}
                    <AnimatePresence mode="popLayout">
                      {filteredRooms.map((room, idx) => (
                        <motion.tr
                          key={room.id}
                          initial={shouldAnimateRoomsTable ? { opacity: 0, y: 20 } : false}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20, transition: { duration: 0.2 } }}
                          transition={shouldAnimateRoomsTable ? { 
                            delay: idx * 0.05,
                            opacity: { duration: 0.3 }
                          } : {
                            opacity: { duration: 0.2 }
                          }}
                          className="border-t border-gray-200 dark:border-gray-700 hover:bg-cyan-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                        <td className="py-3 pr-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span>{room.name}</span>
                          {room.status === 'in_progress' && (
                              <span className="px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-300 rounded-full font-medium">
                              In Progress
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">{room.subject}</td>
                      <td className="py-3 pr-4">{room.difficulty}</td>
                      <td className="py-3 pr-4">{room.players}/{room.capacity}</td>
                      <td className="py-3 pr-4">
                        {room.visibility}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                            <motion.button
                            onClick={async () => {
                              try {
                                const snap = await fetch(`${baseUrl}/rooms/${encodeURIComponent(room.id)}`);
                                if (!snap.ok) throw new Error('Room not found');
                                const s = await snap.json();
                                if ((s?.clients ?? 0) >= (s?.maxClients ?? 0)) {
                                  setRoomsError('Room is full');
                                  return;
                                }
                                handleJoinListedRoom(room.id);
                              } catch (err) {
                                setRoomsError('Unable to join room');
                              }
                            }}
                            disabled={isInRoom || isCheckingMembership}
                              className="py-2.5 px-5 bg-[#34c759] text-white rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                          >
                            {isCheckingMembership ? 'Checking...' : isInRoom ? 'Already in Room' : 'Join'}
                            </motion.button>
                        </div>
                      </td>
                      </motion.tr>
                  ))}
                    </AnimatePresence>
                </tbody>
              </table>
            </div>
            </Card>
          </div>
        </div>
      </div>
      {/* Difficulty Selection Popup */}
      {showDifficultyPopup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setShowDifficultyPopup(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-3xl shadow-2xl p-8 w-full max-w-md mx-4 text-center border border-white/20 dark:border-gray-700/50"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-zonaProUltra dark:text-gray-200 mb-2" style={{ color: isDarkMode ? '#ff7a00' : '#0a1a2f', lineHeight: '1.2', paddingBottom: '0.25rem' }}>Select Difficulty</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 mb-6 font-zonaProRegular">Choose the difficulty level for your room</p>

            <div className="space-y-3 mb-6">
              <motion.button
                onClick={() => { setNewRoomDifficulty('random'); setHasChosenDifficulty(true); setShowDifficultyPopup(false); }}
                className="w-full py-3 px-6 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 text-purple-800 dark:text-purple-200 text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                🎲 Random (Mixed Difficulty)
              </motion.button>
              <motion.button
                onClick={() => { setNewRoomDifficulty('Easy'); setHasChosenDifficulty(true); setShowDifficultyPopup(false); }}
                className="w-full py-3 px-6 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 text-green-800 dark:text-green-200 text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                🟢 Easy (+100/-100 points)
              </motion.button>
              <motion.button
                onClick={() => { setNewRoomDifficulty('Medium'); setHasChosenDifficulty(true); setShowDifficultyPopup(false); }}
                className="w-full py-3 px-6 bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200 text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                🟡 Medium (+200/-200 points)
              </motion.button>
              <motion.button
                onClick={() => { setNewRoomDifficulty('Hard'); setHasChosenDifficulty(true); setShowDifficultyPopup(false); }}
                className="w-full py-3 px-6 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-800 dark:text-red-200 text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                🔴 Hard (+300/-300 points)
              </motion.button>
          </div>

            <motion.button
              onClick={() => setShowDifficultyPopup(false)}
              className="w-full py-2 px-6 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full transition-colors duration-150"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </motion.button>
          </motion.div>
        </motion.div>
      )}
      {isCreating && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-2xl shadow-2xl p-6 w-[90%] max-w-md text-center border border-white/20 dark:border-gray-700/50"
          >
            <h4 className="text-xl sm:text-2xl md:text-3xl font-zonaProUltra dark:text-gray-200 mb-2" style={{ lineHeight: '1.2', paddingBottom: '0.25rem' }}>Creating room…</h4>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 font-zonaProRegular">Please wait while we set things up.</p>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default MultiplayerLobby;